import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzConfigService } from 'ng-zorro-antd/core/config';
import { resolveAcceptLanguage } from 'resolve-accept-language';
import { BehaviorSubject } from 'rxjs';
import { Language, languages } from '../config/languages';
import { PreferredLanguageKey } from '../config/constants';
import { WindowService } from './window.service';

type Direction = Language['dir'];

@Injectable({ providedIn: 'root' })
export class I18nService {
  defaultLang = 'en';
  defaultLocale = 'en-US';
  language = this.defaultLang;
  direction: Direction = 'ltr';
  private readonly doc = inject(DOCUMENT);
  private readonly directionSubject = new BehaviorSubject<Direction>(this.direction);
  private readonly languageSubject = new BehaviorSubject<string>(this.language);
  readonly direction$ = this.directionSubject.asObservable();
  readonly language$ = this.languageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private windowService: WindowService,
    private nzConfigService: NzConfigService,
  ) {}

  private readonly languageMap = new Map(languages.map((language) => [language.code, language]));
  private readonly langCodes: string[] = languages.map((l) => l.code);
  private readonly allowedLocales: readonly string[] = languages.map((l) => l.locale);
  private readonly codeByLocale: Record<string, string> = Object.fromEntries(languages.map((l) => [l.locale, l.code]));

  init() {
    this.translate.addLangs(this.langCodes);
    this.translate.setDefaultLang(this.defaultLang);
    this.changeLanguage(this.resolveInitialLanguage());
  }

  changeLanguage(lang: string) {
    const activeLanguage = this.getLanguageConfig(lang);
    this.translate.use(activeLanguage.code);
    this.windowService.setStorage(PreferredLanguageKey, activeLanguage.code);
    this.language = activeLanguage.code;
    this.direction = activeLanguage.dir;
    this.languageSubject.next(this.language);
    this.directionSubject.next(this.direction);
    this.updateDocumentLanguage(activeLanguage);
    this.nzConfigService.set('modal', { nzDirection: this.direction });
  }

  private resolveInitialLanguage(): string {
    const queryLang = this.getQueryParamLang();
    if (queryLang) {
      return queryLang;
    }

    const storedLang = this.windowService.getStorage(PreferredLanguageKey);
    if (storedLang && this.langCodes.includes(storedLang)) {
      return storedLang;
    }

    return this.detectAcceptLanguage();
  }

  private getQueryParamLang(): string | null {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    return lang && this.langCodes.includes(lang) ? lang : null;
  }

  private detectAcceptLanguage(): string {
    const acceptLanguageHeader = navigator.languages ? navigator.languages.join(',') : navigator.language;
    const result = resolveAcceptLanguage(acceptLanguageHeader, this.allowedLocales, this.defaultLocale);
    const matched = (typeof result === 'string' ? result : (result as any)) ?? this.defaultLocale;

    // Normalize casing to language-REGION
    const lower = matched.toLowerCase();
    let locale: string;

    if (lower.includes('-')) {
      const [lang, region] = lower.split('-');
      locale = `${lang}-${region.toUpperCase()}`; // e.g. ro-ro -> ro-RO
    } else {
      // Expand bare language ("ro") to first allowed locale starting with it
      locale = this.allowedLocales.find((l) => l.toLowerCase().startsWith(`${lower}-`)) ?? this.defaultLocale; // e.g. ro -> ro-RO
    }

    return this.codeByLocale[locale] ?? this.defaultLang;
  }

  get isRtl(): boolean {
    return this.direction === 'rtl';
  }

  private getLanguageConfig(lang: string): Language {
    return this.languageMap.get(lang) ?? this.languageMap.get(this.defaultLang)!;
  }

  private updateDocumentLanguage(activeLanguage: Language): void {
    this.doc.documentElement.lang = activeLanguage.code;
    this.doc.documentElement.dir = activeLanguage.dir;
    if (this.doc.body) {
      this.doc.body.dir = activeLanguage.dir;
    }
  }
}
